import { usd } from '../../../utilities/format';
import { CERTIFICATE_TEMPLATES, truncateName } from './templates';
import { t } from '../../../i18n/i18n';
import './CertificateArt.scss';

/**
 * One certificate, drawn.
 *
 * The artwork is a template picked by the row's `type`; the name, the date and
 * — on the two payout certificates — the amount are laid over it from
 * `templates.js`. The same component is the grid thumbnail, the hero preview
 * and the full-size view: nothing about it is sized in pixels, so it is the
 * caller's box that decides how big the certificate is.
 *
 * A `type` with no template falls back to the server-rendered PNG on `link`,
 * so a certificate the backend starts issuing before this map knows about it
 * still appears rather than vanishing.
 */

/** The S3 key is built from the trader's name, so `link` arrives with raw
 * spaces and, on some rows, a raw `#` (".../Sakshi Rane_D#1600453.png"). Used
 * as-is the `#` starts the fragment and the request truncates to a key that
 * 404s, so percent-encode the filename. The raw spaces are the proof the rest
 * of the key is unencoded, so this cannot double-encode. */
export const certificateSrc = (link) => {
  if (!link) return '';
  if (typeof link !== 'string') {
    if (typeof link === 'object' && link !== null) {
      const candidate = link.url || link.link || link.certificate || link.src || '';
      return typeof candidate === 'string' ? certificateSrc(candidate) : '';
    }
    return '';
  }
  const cut = link.lastIndexOf('/');
  return cut === -1 ? link : `${link.slice(0, cut + 1)}${encodeURIComponent(link.slice(cut + 1))}`;
};

/** What each field key reads off the row. */
const fieldText = (key, item) => {
  if (key === 'name') return truncateName(item.name);
  if (key === 'amount') return usd(item.amount, { decimals: 2 });
  return item.created_at ?? '';
};

/**
 * A field's box. `top`/`bottom` and `left` are percentages of the artwork;
 * `size` and `width` are percentages of its width, which is what `cqw` means
 * against the container query on `.cert-art` — so the whole layout scales with
 * the certificate instead of with the viewport.
 */
const fieldStyle = ({ top, bottom, left, width, size, font, weight, color }) => ({
  ...(top != null && { top: `${top}%` }),
  ...(bottom != null && { bottom: `${bottom}%` }),
  left: `${left}%`,
  ...(width != null && { width: `${width}cqw` }),
  fontSize: `${size}cqw`,
  ...(font && { fontFamily: font }),
  ...(weight && { fontWeight: weight }),
  color,
});

export default function CertificateArt({ item, className = '', loading }) {
  const template = CERTIFICATE_TEMPLATES[item.type];

  if (!template) {
    return (
      <div className={`cert-art ${className}`.trim()}>
        <img
          className="cert-art__bg"
          src={certificateSrc(item.link)}
          alt={t(item.title)}
          loading={loading}
        />
      </div>
    );
  }

  return (
    // The artwork is decoration and the fields over it are the content, so the
    // name goes on the frame and the `img` is left out of the tree entirely.
    <div className={`cert-art ${className}`.trim()} role="img" aria-label={t(item.title)}>
      <img className="cert-art__bg" src={template.image} alt="" loading={loading} />

      {template.fields.map((field) => (
        <span key={field.key} className="cert-art__field" style={fieldStyle(field)}>
          {fieldText(field.key, item)}
        </span>
      ))}
    </div>
  );
}
